import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Panel } from "@/components/Panel";
import { Paginacion } from "@/components/Paginacion";
import { ResumenTrabajoDialog } from "@/components/ResumenTrabajoDialog";
import { FotosTrabajo } from "@/components/FotosTrabajo";
import { GarantiaReporteDialog } from "@/components/GarantiaReporteDialog";
import { Search, Wrench, FileText, Ban, Calculator, PackagePlus, ClipboardCheck, ClipboardList, Truck, Send, Camera, ShieldCheck } from "lucide-react";

export const numTrabajo = (n) => String(n ?? 0).padStart(6, "0");
export const fmtFecha = (iso) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
export const tiempoTranscurrido = (iso) => {
  const ms = Date.now() - new Date(iso).getTime();
  const horas = Math.floor(ms / 3600000);
  const dias = Math.floor(horas / 24);
  const h = horas % 24;
  return dias > 0 ? `${dias} Día${dias > 1 ? "s" : ""} ${h} Hora${h !== 1 ? "s" : ""}` : `${horas} Hora${horas !== 1 ? "s" : ""}`;
};

const HONDA_ESTADO_LABEL = {
  pendiente: "Pendiente", en_proceso: "En proceso", esperando_usuario: "Esperando respuesta",
  creando: "Creando", creada: "Creada", simulado: "Simulado", error: "Error", incierto: "Incierto",
  bloqueada: "Bloqueada en Honda",
};
const HONDA_ESTADO_CLS = {
  pendiente: "bg-slate-100 text-slate-600", en_proceso: "bg-sky-100 text-sky-700",
  esperando_usuario: "bg-amber-100 text-amber-800", creando: "bg-sky-100 text-sky-700",
  creada: "bg-emerald-100 text-emerald-700", simulado: "bg-violet-100 text-violet-700",
  error: "bg-red-100 text-red-700", incierto: "bg-amber-100 text-amber-800",
  bloqueada: "bg-amber-100 text-amber-800",
};

export const descargarPdf = async (url, nombre) => {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const u = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = u; a.download = nombre; a.click();
    URL.revokeObjectURL(u);
  } catch (e) { toast.error(errorMsg(e)); }
};

export default function TrabajosActivos() {
  const navigate = useNavigate();
  const { tienePermiso } = useAuth();
  const [trabajos, setTrabajos] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1 });
  const [cargando, setCargando] = useState(false);
  const [cuponServicioIds, setCuponServicioIds] = useState(new Set());
  const [hondaDialogTrabajo, setHondaDialogTrabajo] = useState(null);
  const [hondaOrden, setHondaOrden] = useState(null);
  const [hondaCargando, setHondaCargando] = useState(false);
  const [hondaAccionCargando, setHondaAccionCargando] = useState(false);
  const [hondaContactoAbierto, setHondaContactoAbierto] = useState(false);
  const [hondaContactoNombre, setHondaContactoNombre] = useState("");
  const [hondaGuardarContacto, setHondaGuardarContacto] = useState(true);
  const [hondaRespuestaSel, setHondaRespuestaSel] = useState("");
  const [hondaNumeroInput, setHondaNumeroInput] = useState("");
  const [hondaAsesorCargando, setHondaAsesorCargando] = useState(false);
  const [hondaPendienteAbierto, setHondaPendienteAbierto] = useState(false);
  const [hondaPendienteTipo, setHondaPendienteTipo] = useState("orden_abierta");
  const [hondaPendienteOrden, setHondaPendienteOrden] = useState("");
  const [hondaPendienteTexto, setHondaPendienteTexto] = useState("");
  const [soloSinOrdenHonda, setSoloSinOrdenHonda] = useState(false);
  const [resumenTrabajoId, setResumenTrabajoId] = useState(null);
  const [fotosTrabajoId, setFotosTrabajoId] = useState(null);
  const [fotosConteos, setFotosConteos] = useState({});
  const [garantiaTrabajoId, setGarantiaTrabajoId] = useState(null);
  const [hondaEstados, setHondaEstados] = useState({});
  const trabajosRef = useRef([]);
  const metaRef = useRef({ total: 0, total_pages: 1 });
  useEffect(() => { trabajosRef.current = trabajos; }, [trabajos]);
  useEffect(() => { metaRef.current = meta; }, [meta]);

  // silencioso=true: no muestra "Cargando…" y solo redibuja la tabla si cambió algo relevante
  // (para no reiniciar el efecto de fotos de cada fila cada 20 s sin necesidad).
  const cargar = useCallback(async (silencioso = false) => {
    if (!silencioso) setCargando(true);
    try {
      const r = await api.get("/trabajos", { params: { estado: "activo", q: q.trim() || undefined, page, page_size: pageSize } });
      const items = r.data.items;
      if (silencioso) {
        const prevT = trabajosRef.current;
        const cambio = prevT.length !== items.length || metaRef.current.total !== r.data.total ||
          items.some((it, i) => !prevT[i] || prevT[i].id !== it.id || prevT[i].orden_honda !== it.orden_honda ||
            JSON.stringify(prevT[i].orden_honda_pendiente || null) !== JSON.stringify(it.orden_honda_pendiente || null));
        if (cambio) setTrabajos(items);
      } else {
        setTrabajos(items);
      }
      setMeta({ total: r.data.total, total_pages: r.data.total_pages });

      // Estado del robot Honda para las filas sin orden todavía (insignia en vivo).
      const puedeHonda = tienePermiso("honda.ordenes") || tienePermiso("trabajos.orden_honda");
      if (!puedeHonda) { setHondaEstados({}); return; }
      const ids = items.filter((t) => ((t.tareas || []).some((ta) => cuponServicioIds.has(ta.servicio_id)) || t.tiene_garantia)
        && !t.orden_honda).map((t) => t.id);
      if (!ids.length) { setHondaEstados({}); return; }
      try {
        const { data } = await api.get("/honda-ordenes/activas", { params: { trabajo_ids: ids.join(",") } });
        setHondaEstados(data || {});
      } catch { setHondaEstados({}); }
    } finally { if (!silencioso) setCargando(false); }
  }, [q, page, pageSize, cuponServicioIds, tienePermiso]);
  useEffect(() => { setPage(1); }, [q, pageSize]);
  useEffect(() => { const t = setTimeout(cargar, 300); return () => clearTimeout(t); }, [cargar]);

  // Servicios de cupón de cualquier grupo de cilindrada, para mostrar la insignia de Orden
  // Honda en los trabajos que tengan una tarea de cupón (visible para cualquier usuario).
  useEffect(() => {
    api.get("/grupos-cilindrada").then(({ data }) => {
      const ids = new Set();
      (data || []).forEach((g) => (g.cupones || []).forEach((c) => ids.add(c.servicio_id)));
      setCuponServicioIds(ids);
    }).catch(() => {});
  }, []);

  const tieneCupon = (t) => (t?.tareas || []).some((ta) => cuponServicioIds.has(ta.servicio_id));
  const tieneGarantia = (t) => !!t?.tiene_garantia;

  // Insignia "Orden Honda" de cada fila: prioriza lo que ya quedó en el trabajo (orden/pendiente)
  // y, si no hay nada todavía, lo que reporta el robot (hondaEstados) para esa orden en curso.
  const HONDA_ACTIVOS_ROBOT = ["pendiente", "en_proceso", "creando"];
  const hondaBadge = (t) => {
    if (t.orden_honda) return { texto: `Honda #${t.orden_honda}`, cls: "bg-emerald-100 text-emerald-700" };
    if (t.orden_honda_pendiente) return { texto: "Orden pendiente", cls: "bg-amber-100 text-amber-800" };
    const he = hondaEstados[t.id];
    if (he?.estado === "esperando_usuario") {
      return { texto: he.pregunta_tipo === "elegir_vehiculo" ? "Elegir chasis" : "Responder al robot",
        cls: "bg-amber-100 text-amber-800" };
    }
    if (HONDA_ACTIVOS_ROBOT.includes(he?.estado)) {
      return { texto: "Creando en Honda…", cls: "bg-sky-100 text-sky-700" };
    }
    if (he?.estado === "error" || he?.estado === "incierto") {
      return { texto: "Error orden Honda", cls: "bg-red-100 text-red-700", title: he.mensaje || undefined };
    }
    return { texto: "Falta orden Honda", cls: "bg-rose-50 text-rose-600" };
  };

  // Fotos del trabajo (etapa 2): las sube el asesor, cuenta obligatorias cumplidas/total.
  useEffect(() => {
    if (!tienePermiso("trabajos.fotos") || !trabajos.length) { setFotosConteos({}); return; }
    let activo = true;
    Promise.all(trabajos.map((t) => api.get(`/trabajos/${t.id}/fotos`)
      .then(({ data }) => [t.id, data]).catch(() => [t.id, null])))
      .then((pares) => {
        if (!activo) return;
        const mapa = {};
        pares.forEach(([id, data]) => {
          if (!data) return;
          const obligatoriosIngreso = (data.obligatorios || []).filter((o) => o.momento === "ingreso");
          const total = obligatoriosIngreso.length;
          const cumplidas = obligatoriosIngreso.filter((o) => o.cumplido).length;
          mapa[id] = { total, cumplidas };
        });
        setFotosConteos(mapa);
      });
    return () => { activo = false; };
  }, [trabajos, tienePermiso]);

  // silencioso=true evita el parpadeo de "Cargando…" durante el refresco automático cada 5 s.
  const cargarHondaDetalle = async (trabajoId, silencioso = false) => {
    if (!silencioso) setHondaCargando(true);
    try {
      const { data } = await api.get(`/trabajos/${trabajoId}/honda-orden`);
      setHondaOrden(Object.keys(data || {}).length ? data : null);
    } catch { setHondaOrden(null); }
    finally { if (!silencioso) setHondaCargando(false); }
  };

  const puedeVerHondaRobot = tienePermiso("honda.ordenes") || tienePermiso("trabajos.orden_honda");

  const abrirHonda = (t) => {
    setHondaDialogTrabajo(t);
    setHondaContactoAbierto(false);
    setHondaRespuestaSel("");
    setHondaNumeroInput("");
    setHondaPendienteAbierto(false);
    setHondaPendienteTipo("orden_abierta");
    setHondaPendienteOrden("");
    setHondaPendienteTexto("");
    if (puedeVerHondaRobot) cargarHondaDetalle(t.id); else setHondaOrden(null);
  };

  const guardarOrdenHondaAsesor = async () => {
    if (!hondaDialogTrabajo || !hondaNumeroInput.trim()) return;
    setHondaAsesorCargando(true);
    try {
      await api.patch(`/trabajos/${hondaDialogTrabajo.id}/orden-honda`, { orden_honda: hondaNumeroInput.trim() });
      toast.success("Número de orden Honda guardado");
      setHondaDialogTrabajo((prev) => prev && ({ ...prev, orden_honda: hondaNumeroInput.trim(), orden_honda_pendiente: null }));
      setHondaNumeroInput("");
      cargar();
    } catch (err) { toast.error(errorMsg(err)); }
    finally { setHondaAsesorCargando(false); }
  };

  const guardarOrdenHondaPendiente = async () => {
    if (!hondaDialogTrabajo) return;
    if (hondaPendienteTipo === "otro" && !hondaPendienteTexto.trim()) {
      toast.error("Describa el motivo"); return;
    }
    setHondaAsesorCargando(true);
    try {
      const body = { tipo: hondaPendienteTipo,
        orden_bloqueante: hondaPendienteTipo === "orden_abierta" ? (hondaPendienteOrden.trim() || null) : null,
        texto: hondaPendienteTipo === "otro" ? hondaPendienteTexto.trim() : null };
      const { data } = await api.patch(`/trabajos/${hondaDialogTrabajo.id}/orden-honda-pendiente`, body);
      toast.success("Orden Honda marcada como pendiente");
      setHondaPendienteAbierto(false);
      setHondaDialogTrabajo((prev) => prev && ({ ...prev, orden_honda_pendiente: data.orden_honda_pendiente }));
      cargar();
    } catch (err) { toast.error(errorMsg(err)); }
    finally { setHondaAsesorCargando(false); }
  };

  const cerrarHonda = () => {
    setHondaDialogTrabajo(null); setHondaOrden(null); setHondaContactoAbierto(false);
    cargar(); // refresca también la insignia de estado en la fila de la tabla
  };

  // Mientras algún trabajo de la página esté siendo creado por el robot (y no haya ningún
  // diálogo abierto), refresca la tabla cada 20 s para que la insignia se actualice sola.
  useEffect(() => {
    const hayTrabajando = trabajos.some((t) => HONDA_ACTIVOS_ROBOT.includes(hondaEstados[t.id]?.estado));
    const hayDialogoAbierto = !!(hondaDialogTrabajo || resumenTrabajoId || fotosTrabajoId || garantiaTrabajoId);
    if (!hayTrabajando || hayDialogoAbierto) return undefined;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") cargar(true);
    }, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trabajos, hondaEstados, hondaDialogTrabajo, resumenTrabajoId, fotosTrabajoId, garantiaTrabajoId, cargar]);

  // Al volver a la pestaña, un refresco silencioso inmediato (no esperar los 20 s).
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") cargar(true); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [cargar]);

  // Mientras el diálogo esté abierto y el estado sea pendiente/en_proceso/creando, refrescar
  // cada 5 s (el robot de la parte 2 puede estar procesando la orden).
  useEffect(() => {
    if (!hondaDialogTrabajo || !["pendiente", "en_proceso", "creando"].includes(hondaOrden?.estado)) return;
    const t = setInterval(() => cargarHondaDetalle(hondaDialogTrabajo.id, true), 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hondaDialogTrabajo, hondaOrden?.estado]);

  const hondaCrear = async (contactoNombre, guardarContacto) => {
    if (!hondaDialogTrabajo) return;
    setHondaAccionCargando(true);
    try {
      await api.post(`/trabajos/${hondaDialogTrabajo.id}/honda-orden`,
        { contacto_nombre: contactoNombre || null, guardar_contacto: !!guardarContacto });
      setHondaContactoAbierto(false);
      toast.success("Orden de cupón Honda encolada");
      await cargarHondaDetalle(hondaDialogTrabajo.id);
      cargar();
    } catch (err) {
      if (err?.response?.data?.detail?.falta === "contacto") {
        setHondaContactoNombre(""); setHondaGuardarContacto(true);
        setHondaContactoAbierto(true);
      } else {
        toast.error(errorMsg(err));
      }
    } finally { setHondaAccionCargando(false); }
  };

  const hondaReintentar = async () => {
    if (!hondaOrden) return;
    setHondaAccionCargando(true);
    try {
      await api.post(`/honda-ordenes/${hondaOrden.id}/reintentar`);
      await cargarHondaDetalle(hondaDialogTrabajo.id);
      cargar();
    } catch (err) { toast.error(errorMsg(err)); }
    finally { setHondaAccionCargando(false); }
  };

  const hondaResponder = async (opcion) => {
    if (!hondaOrden) return;
    setHondaAccionCargando(true);
    try {
      await api.post(`/honda-ordenes/${hondaOrden.id}/responder`, { opcion });
      await cargarHondaDetalle(hondaDialogTrabajo.id);
      cargar();
    } catch (err) { toast.error(errorMsg(err)); }
    finally { setHondaAccionCargando(false); }
  };

  const verCaptura = async () => {
    if (!hondaOrden?.id) return;
    try {
      const r = await api.get(`/honda-ordenes/${hondaOrden.id}/captura`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      window.open(u, "_blank");
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const visibles = soloSinOrdenHonda
    ? trabajos.filter((t) => (tieneCupon(t) || tieneGarantia(t)) && !t.orden_honda && !t.orden_honda_pendiente)
    : trabajos;

  const abrirCotizacion = (t) => navigate(`/cotizaciones/c/${t.cotizacion_origen_id}`);

  const anular = async (t) => {
    if (!window.confirm("¿Desea anular el trabajo? Se dará por terminado y no se podrán realizar más cambios")) return;
    try { await api.post(`/trabajos/${t.id}/anular`); toast.success("Trabajo anulado"); cargar(); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  return (
    <div data-testid="trabajos-activos-page" className="space-y-4">
      <Panel titulo="Trabajos Activos" testId="trabajos-activos-panel">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative max-w-md flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="trabajos-buscar-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por número, cliente o placa..." className="pl-9" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
            <Checkbox data-testid="filtro-sin-orden-honda-checkbox" checked={soloSinOrdenHonda}
              onCheckedChange={(v) => setSoloSinOrdenHonda(!!v)} />
            Cupones sin orden Honda
          </label>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="trabajos-activos-tabla" className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                <th className="py-2.5 pr-3 font-semibold">Número</th>
                <th className="py-2.5 pr-3 font-semibold">Cliente</th>
                <th className="py-2.5 pr-3 font-semibold">Marca</th>
                <th className="py-2.5 pr-3 font-semibold">Modelo</th>
                <th className="py-2.5 pr-3 font-semibold">Placa</th>
                <th className="py-2.5 pr-3 font-semibold">Fecha de Ingreso</th>
                <th className="py-2.5 pr-3 font-semibold">Tiempo Transcurrido</th>
                <th className="py-2.5 text-right font-semibold">Opciones</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((t) => { const bHonda = hondaBadge(t); return (
                <tr key={t.id} data-testid={`trabajo-row-${t.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2.5 pr-3 font-medium text-indigo-700">
                    <button type="button" data-testid={`trabajo-numero-btn-${t.id}`}
                      onClick={(e) => { e.stopPropagation(); setResumenTrabajoId(t.id); }}
                      className="hover:underline underline-offset-2">
                      {numTrabajo(t.numero)}
                    </button>
                  </td>
                  <td className="py-2.5 pr-3 text-slate-800">{t.cliente?.nombre || "—"}</td>
                  <td className="py-2.5 pr-3">{t.vehiculo?.marca || "—"}</td>
                  <td className="py-2.5 pr-3">{t.vehiculo?.modelo || "—"}</td>
                  <td className="py-2.5 pr-3 font-medium">{t.vehiculo?.placa || "—"}</td>
                  <td className="py-2.5 pr-3 text-slate-600">{fmtFecha(t.fecha_ingreso)}</td>
                  <td className="py-2.5 pr-3">
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-50 text-amber-700">
                      {tiempoTranscurrido(t.fecha_ingreso)}
                    </span>
                  </td>
                  <td className="py-2.5">
                    <div className="flex items-center justify-end gap-1.5">
                      {tienePermiso("trabajos.trabajar") && (
                      <button data-testid={`trabajo-trabajar-btn-${t.id}`} title="Trabajar / Ver"
                        onClick={() => navigate(`/trabajos/${t.id}/finalizar`)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-indigo-50 text-indigo-600 hover:bg-indigo-100">
                        <Wrench size={13} />
                      </button>
                      )}
                      {tienePermiso("trabajos.solicitar") && (
                      <button data-testid={`trabajo-solicitud-btn-${t.id}`} title="Solicitud de repuestos y servicios"
                        onClick={() => navigate(`/trabajos/${t.id}/solicitud`)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-teal-50 text-teal-600 hover:bg-teal-100">
                        <PackagePlus size={13} />
                      </button>
                      )}
                      {tienePermiso("trabajos.aprobar") && (
                      <button data-testid={`trabajo-aprobacion-btn-${t.id}`} title="Aprobar solicitudes"
                        onClick={() => navigate(`/trabajos/${t.id}/aprobacion`)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100">
                        <ClipboardCheck size={13} />
                      </button>
                      )}
                      {tienePermiso("cotizaciones.crear") && t.cotizacion_origen_id && (
                      <button data-testid={`trabajo-cotizacion-btn-${t.id}`}
                        title="Abrir cotización"
                        onClick={() => abrirCotizacion(t)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-teal-50 text-teal-600 hover:bg-teal-100">
                        <Calculator size={13} />
                      </button>
                      )}
                      {tienePermiso("boletas.ver") && (
                      <button data-testid={`trabajo-boleta-btn-${t.id}`} title="Boleta de ingreso (PDF)"
                        onClick={() => descargarPdf(`/trabajos/${t.id}/boleta-pdf`, `boleta_${numTrabajo(t.numero)}.pdf`)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-50 text-amber-600 hover:bg-amber-100">
                        <FileText size={13} />
                      </button>
                      )}
                      {tienePermiso("boletas.ver") && (
                      <button data-testid={`trabajo-reporte-mecanicos-btn-${t.id}`} title="Reporte para mecánicos (PDF)"
                        onClick={() => descargarPdf(`/trabajos/${t.id}/tareas-pdf`, `reporte_mecanicos_${numTrabajo(t.numero)}.pdf`)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-sky-50 text-sky-600 hover:bg-sky-100">
                        <ClipboardList size={13} />
                      </button>
                      )}
                      {tienePermiso("pedidos.crear") && (
                      <button data-testid={`trabajo-pedido-especial-btn-${t.id}`} title="Crear pedido especial"
                        onClick={() => navigate("/pedidos-especiales/nuevo", { state: {
                          trabajoId: t.id, clienteId: t.cliente?.id, vehiculoId: t.vehiculo?.id } })}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-violet-50 text-violet-600 hover:bg-violet-100">
                        <Truck size={13} />
                      </button>
                      )}
                      {(tieneCupon(t) || tieneGarantia(t)) && (
                      <button data-testid={`trabajo-honda-orden-btn-${t.id}`} title={bHonda.title || "Orden Honda"}
                        onClick={() => abrirHonda(t)}
                        className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${bHonda.cls}`}>
                        <Send size={11} />
                        {bHonda.texto}
                      </button>
                      )}
                      {tienePermiso("trabajos.fotos") && (
                      <button data-testid={`trabajo-fotos-btn-${t.id}`} title="Fotos del trabajo"
                        onClick={() => setFotosTrabajoId(t.id)}
                        className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                          fotosConteos[t.id] && fotosConteos[t.id].cumplidas < fotosConteos[t.id].total
                            ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-600"}`}>
                        <Camera size={11} />
                        {fotosConteos[t.id] ? `${fotosConteos[t.id].cumplidas}/${fotosConteos[t.id].total}` : "Fotos"}
                      </button>
                      )}
                      {tieneGarantia(t) && tienePermiso("honda.ordenes") && (
                      <button data-testid={`trabajo-garantia-btn-${t.id}`} title="Reporte de garantía Honda"
                        onClick={() => setGarantiaTrabajoId(t.id)}
                        className="shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
                        <ShieldCheck size={11} /> Reporte de garantía
                      </button>
                      )}
                      {tienePermiso("trabajos.anular") && (
                      <button data-testid={`trabajo-anular-btn-${t.id}`} title="Anular" onClick={() => anular(t)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100">
                        <Ban size={13} />
                      </button>
                      )}
                    </div>
                  </td>
                </tr>
              ); })}
              {visibles.length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-slate-400" data-testid="trabajos-vacio">
                  No hay trabajos activos
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Paginacion page={page} totalPages={meta.total_pages} total={meta.total} pageSize={pageSize}
          onPage={setPage} onPageSize={setPageSize} cargando={cargando} />
      </Panel>

      <Dialog open={!!hondaDialogTrabajo} onOpenChange={(o) => { if (!o) cerrarHonda(); }}>
        <DialogContent data-testid="honda-orden-dialog" className="max-w-md overflow-hidden">
          <DialogHeader>
            <DialogTitle>Orden Honda — Orden #{hondaDialogTrabajo ? numTrabajo(hondaDialogTrabajo.numero) : ""}</DialogTitle>
          </DialogHeader>
          {hondaDialogTrabajo && (
            <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/70 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Número de orden Honda (asesor)
              </p>
              {hondaDialogTrabajo.orden_honda ? (
                <p data-testid="honda-orden-numero-asesor" className="text-sm font-semibold text-emerald-700">
                  #{hondaDialogTrabajo.orden_honda}
                </p>
              ) : hondaDialogTrabajo.orden_honda_pendiente ? (
                <div className="space-y-1.5">
                  <p data-testid="honda-orden-pendiente-info" className="text-sm text-amber-800">
                    {hondaDialogTrabajo.orden_honda_pendiente.tipo === "otro"
                      ? hondaDialogTrabajo.orden_honda_pendiente.texto
                      : hondaDialogTrabajo.orden_honda_pendiente.orden_bloqueante
                        ? `La moto tiene la orden #${hondaDialogTrabajo.orden_honda_pendiente.orden_bloqueante} abierta en Honda`
                        : "La moto tiene otra orden abierta en Honda"}
                  </p>
                  <p className="text-xs text-slate-400">Pendiente desde {fmtFecha(hondaDialogTrabajo.orden_honda_pendiente.desde)}</p>
                  {tienePermiso("trabajos.orden_honda") && (
                    <div className="flex gap-2 pt-1">
                      <Input data-testid="honda-orden-asesor-numero-input" value={hondaNumeroInput}
                        onChange={(e) => setHondaNumeroInput(e.target.value)} placeholder="N.° de orden Honda" className="h-8 text-sm" />
                      <Button size="sm" data-testid="honda-orden-asesor-guardar-btn" onClick={guardarOrdenHondaAsesor}
                        disabled={hondaAsesorCargando || !hondaNumeroInput.trim()}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0">Guardar</Button>
                    </div>
                  )}
                </div>
              ) : tienePermiso("trabajos.orden_honda") ? (
                !hondaPendienteAbierto ? (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input data-testid="honda-orden-asesor-numero-input" value={hondaNumeroInput}
                        onChange={(e) => setHondaNumeroInput(e.target.value)} placeholder="N.° de orden Honda" className="h-8 text-sm" />
                      <Button size="sm" data-testid="honda-orden-asesor-guardar-btn" onClick={guardarOrdenHondaAsesor}
                        disabled={hondaAsesorCargando || !hondaNumeroInput.trim()}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0">Guardar</Button>
                    </div>
                    <button type="button" data-testid="honda-orden-asesor-marcar-pendiente-btn"
                      onClick={() => setHondaPendienteAbierto(true)}
                      className="text-xs font-medium text-amber-700 underline">Marcar como pendiente</button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <select data-testid="honda-orden-pendiente-tipo-select" value={hondaPendienteTipo}
                      onChange={(e) => setHondaPendienteTipo(e.target.value)}
                      className="w-full h-8 text-sm rounded-md border border-slate-200 px-2">
                      <option value="orden_abierta">La moto tiene otra orden abierta en Honda</option>
                      <option value="otro">Otro motivo</option>
                    </select>
                    {hondaPendienteTipo === "orden_abierta" ? (
                      <Input data-testid="honda-orden-pendiente-orden-input" value={hondaPendienteOrden}
                        onChange={(e) => setHondaPendienteOrden(e.target.value)}
                        placeholder="N.° de la orden bloqueante (opcional)" className="h-8 text-sm" />
                    ) : (
                      <Input data-testid="honda-orden-pendiente-texto-input" value={hondaPendienteTexto}
                        onChange={(e) => setHondaPendienteTexto(e.target.value)}
                        placeholder="Describa el motivo" className="h-8 text-sm" />
                    )}
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" data-testid="honda-orden-pendiente-cancelar-btn"
                        onClick={() => setHondaPendienteAbierto(false)} disabled={hondaAsesorCargando}>Cancelar</Button>
                      <Button size="sm" data-testid="honda-orden-pendiente-guardar-btn" onClick={guardarOrdenHondaPendiente}
                        disabled={hondaAsesorCargando} className="bg-amber-600 hover:bg-amber-700 text-white">
                        {hondaAsesorCargando ? "Guardando..." : "Marcar pendiente"}
                      </Button>
                    </div>
                  </div>
                )
              ) : (
                <p data-testid="honda-orden-falta-numero-aviso" className="text-sm text-rose-600">
                  Falta el número de orden Honda
                </p>
              )}
            </div>
          )}
          {puedeVerHondaRobot && (
          hondaCargando ? (
            <p className="text-sm text-slate-500">Cargando…</p>
          ) : hondaContactoAbierto ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">
                El cliente es una empresa y el vehículo no tiene un contacto guardado. Ingrese el
                nombre de la persona de contacto para la orden de Honda.
              </p>
              <Input data-testid="honda-orden-contacto-nombre-input" value={hondaContactoNombre}
                onChange={(e) => setHondaContactoNombre(e.target.value)} placeholder="Nombre del contacto" autoFocus />
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Checkbox data-testid="honda-orden-contacto-guardar-checkbox" checked={hondaGuardarContacto}
                  onCheckedChange={(v) => setHondaGuardarContacto(!!v)} />
                Guardar como contacto del vehículo
              </label>
              <DialogFooter className="gap-2">
                <Button variant="outline" data-testid="honda-orden-contacto-cancelar-btn"
                  onClick={() => setHondaContactoAbierto(false)} disabled={hondaAccionCargando}>Cancelar</Button>
                <Button data-testid="honda-orden-contacto-enviar-btn"
                  onClick={() => hondaCrear(hondaContactoNombre, hondaGuardarContacto)}
                  disabled={hondaAccionCargando || !hondaContactoNombre.trim()}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  {hondaAccionCargando ? "Enviando..." : "Enviar"}
                </Button>
              </DialogFooter>
            </div>
          ) : !hondaOrden ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">
                {tieneGarantia(hondaDialogTrabajo) && !tieneCupon(hondaDialogTrabajo)
                  ? "Este trabajo todavía no tiene una orden de garantía Honda."
                  : "Este trabajo todavía no tiene una orden de cupón Honda."}
              </p>
              {tienePermiso("honda.ordenes") && (
              <DialogFooter>
                <Button data-testid="honda-orden-crear-btn" onClick={() => hondaCrear(null, false)} disabled={hondaAccionCargando}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  {hondaAccionCargando ? "Creando..." : tieneGarantia(hondaDialogTrabajo) && !tieneCupon(hondaDialogTrabajo)
                    ? "Crear orden de garantía Honda" : "Crear orden"}
                </Button>
              </DialogFooter>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span data-testid="honda-orden-estado-badge"
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold uppercase ${
                    HONDA_ESTADO_CLS[hondaOrden.estado] || "bg-slate-100 text-slate-600"}`}>
                  {HONDA_ESTADO_LABEL[hondaOrden.estado] || hondaOrden.estado}
                </span>
                {hondaOrden.orden_honda && (
                  <span data-testid="honda-orden-numero" className="text-sm font-semibold text-slate-700">
                    #{hondaOrden.orden_honda}
                  </span>
                )}
              </div>
              {hondaOrden.mensaje && (
                <p data-testid="honda-orden-mensaje"
                  className="text-sm text-slate-600 break-words [overflow-wrap:anywhere] max-h-40 overflow-y-auto">
                  {hondaOrden.mensaje}
                </p>
              )}
              {hondaOrden.estado === "incierto" && (
                <p data-testid="honda-orden-incierto-aviso"
                  className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
                  Puede que la orden se haya creado: revisá en Honda antes de reintentar
                </p>
              )}
              {hondaOrden.tiene_captura && (
                <button type="button" data-testid="honda-orden-ver-captura-btn" onClick={verCaptura}
                  className="text-xs font-medium text-indigo-600 underline">Ver captura</button>
              )}
              {hondaOrden.estado === "esperando_usuario" && hondaOrden.pregunta && tienePermiso("honda.ordenes") && (
                <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                  <p data-testid="honda-orden-pregunta-mensaje" className="text-sm font-medium text-amber-800">
                    {hondaOrden.pregunta.mensaje}
                  </p>
                  <div className="space-y-1.5">
                    {(hondaOrden.pregunta.opciones || []).map((op) => (
                      <label key={op.valor} data-testid={`honda-orden-pregunta-opcion-${op.valor}`}
                        className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                        <input type="radio" name="honda-respuesta" checked={hondaRespuestaSel === op.valor}
                          onChange={() => setHondaRespuestaSel(op.valor)} />
                        {op.texto}
                      </label>
                    ))}
                  </div>
                  <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" size="sm" data-testid="honda-orden-cancelar-btn"
                      onClick={() => hondaResponder("cancelar")} disabled={hondaAccionCargando}>Cancelar</Button>
                    <Button size="sm" data-testid="honda-orden-responder-btn"
                      onClick={() => hondaResponder(hondaRespuestaSel)} disabled={hondaAccionCargando || !hondaRespuestaSel}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white">Responder</Button>
                  </div>
                </div>
              )}
              {["error", "simulado", "incierto", "bloqueada"].includes(hondaOrden.estado) && tienePermiso("honda.ordenes") && (
                <DialogFooter>
                  <Button data-testid="honda-orden-reintentar-btn" onClick={hondaReintentar} disabled={hondaAccionCargando}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white">
                    {hondaAccionCargando ? "Reintentando..." : "Reintentar"}
                  </Button>
                </DialogFooter>
              )}
            </div>
          ))}
        </DialogContent>
      </Dialog>

      <ResumenTrabajoDialog trabajoId={resumenTrabajoId} mostrarRealizados={false}
        onClose={() => setResumenTrabajoId(null)} />

      {tienePermiso("trabajos.fotos") && (
        <Dialog open={!!fotosTrabajoId} onOpenChange={(o) => { if (!o) { setFotosTrabajoId(null); cargar(); } }}>
          <DialogContent data-testid="fotos-trabajo-dialog" className="max-w-xl">
            <DialogHeader><DialogTitle>Fotos del trabajo</DialogTitle></DialogHeader>
            <FotosTrabajo trabajoId={fotosTrabajoId} momento="ingreso" soloMomento="ingreso" />
          </DialogContent>
        </Dialog>
      )}

      {tienePermiso("honda.ordenes") && (
        <GarantiaReporteDialog trabajoId={garantiaTrabajoId} onClose={() => setGarantiaTrabajoId(null)} />
      )}
    </div>
  );
}
